import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { vehicleRegistrationKeys } from '@/features/vehiclesearch/services/queryKeys';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { reverseInvoice } from '../services/invoiceReversal';
import { reversalErrorMessage } from '../utils/invoiceReversalErrors';
import { reverseAvailability } from '../utils/invoiceReversal';
import { agreementKeys } from './agreementKeys';
import type { CashPositionWatch } from './useCashPositionWatch';
import type { AgreementInvoice } from '../types/agreements.types';

export interface InvoiceReversal {
    canReverse: (invoice: AgreementInvoice) => boolean;
    reverse: (invoice: AgreementInvoice, note: string) => Promise<boolean>;
    pending: boolean;
    /** The balance is still catching up with a reversal this session raised. */
    settling: boolean;
    errorMessage: string | null;
    clearError: () => void;
}

interface Options {
    agreementId: string | undefined;
    vehicleId: string | null | undefined;
    agreementActive: boolean;
    candidateDealerIds: readonly string[];
    watch: Pick<CashPositionWatch, 'posting' | 'startWatch'>;
}

type ReleaseStage = 'idle' | 'armed' | 'watching';

/** The ledger reversal and the consumers releasing items, writing the note and restoring mileage run asynchronously. */
const refreshConsumerOutcomes = (queryClient: QueryClient, agreementId: string, vehicleId: string | null | undefined) =>
    Promise.all([
        queryClient.invalidateQueries({ queryKey: agreementKeys.financials(agreementId) }),
        queryClient.invalidateQueries({ queryKey: agreementKeys.soldItems(agreementId) }),
        queryClient.invalidateQueries({ queryKey: agreementKeys.notes(agreementId) }),
        queryClient.invalidateQueries({ queryKey: agreementKeys.history(agreementId) }),
        ...(vehicleId ? [queryClient.invalidateQueries({ queryKey: vehicleRegistrationKeys.detail(vehicleId) })] : []),
    ]);

export function useInvoiceReversal({ agreementId, vehicleId, agreementActive, candidateDealerIds, watch }: Options): InvoiceReversal {
    const { isInternal } = useAuth();
    const queryClient = useQueryClient();
    const [releaseStage, setReleaseStage] = useState<ReleaseStage>('idle');
    const { posting, startWatch } = watch;

    const mutation = useMutation<AgreementInvoice, unknown, { invoice: AgreementInvoice; note: string }>({
        mutationFn: ({ invoice, note }) => reverseInvoice(agreementId ?? '', invoice.id, note),
        onSuccess: async () => {
            setReleaseStage('armed');
            startWatch(null);
            const id = agreementId ?? '';
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: agreementKeys.invoices(id) }),
                refreshConsumerOutcomes(queryClient, id, vehicleId),
            ]);
        },
    });

    useEffect(() => {
        if (posting === 'posting' && releaseStage === 'armed') {
            setReleaseStage('watching');
        } else if (posting !== 'posting' && releaseStage === 'watching') {
            setReleaseStage('idle');
            void refreshConsumerOutcomes(queryClient, agreementId ?? '', vehicleId);
        }
    }, [posting, releaseStage, agreementId, vehicleId, queryClient]);

    // The shared watch holds one baseline, so a second reversal waits until it settles.
    const busy = mutation.isPending || posting === 'posting';
    const canReverse = useCallback(
        (invoice: AgreementInvoice) =>
            !busy && reverseAvailability({ invoice, isInternal, candidateDealerIds, agreementActive }),
        [busy, isInternal, candidateDealerIds, agreementActive],
    );

    const { mutateAsync, reset } = mutation;
    const reverse = useCallback(
        async (invoice: AgreementInvoice, note: string) => {
            try {
                await mutateAsync({ invoice, note });
                return true;
            } catch {
                return false;
            }
        },
        [mutateAsync],
    );

    return {
        canReverse,
        reverse,
        pending: mutation.isPending,
        settling: posting === 'posting' && releaseStage !== 'idle',
        errorMessage: mutation.isError ? reversalErrorMessage(mutation.error) : null,
        clearError: reset,
    };
}
